import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Sparkles, 
  Brain, 
  TrendingUp, 
  Mic, 
  Target, 
  Shield,
  Zap,
  Clock,
  DollarSign,
  Award,
  Users,
  CheckCircle,
  X,
  Rocket
} from 'lucide-react';

const Roadmap = ({ onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const features = [
    // NEUE FEATURES - Nicht implementiert (durch Resets verloren)
    {
      id: 'sales-coaching',
      title: "Sales-Coaching-System",
      icon: <Target className="w-8 h-8" />,
      category: "sales",
      priority: "high",
      complexity: "high",
      estimatedTime: "3-4 Stunden",
      status: "planned",
      description: "Geführtes Entscheidungssystem für souveräne Verkaufsgespräche",
      features: [
        "Step 0: Sales-Kompass (7-Fragen-Fragebogen zur KI-Kalibrierung)",
        "Sales-Coach Toggle (global AN/AUS)",
        "CustomerNeeds in Step 2 (Ziel, Priorität, Pain Point)",
        "Live-Coaching Widget in Step 3 (Hinweise, JA-Fragen, Abschluss)",
        "Argumentation-Blöcke auf Landing Page (Was wichtig war, Warum dieses Paket)",
        "Nachfass-Vorlagen (24h, 72h, 7 Tage)",
        "KI-Integration (Emergent LLM Key)",
        "Breadcrumb 0-5 statt 1-5"
      ]
    },
    {
      id: 'landing-page-improvements',
      title: "Landing Page Verbesserungen (9 Features)",
      icon: <Sparkles className="w-8 h-8" />,
      category: "ux",
      priority: "high",
      complexity: "medium",
      estimatedTime: "2 Stunden",
      status: "planned",
      description: "Interaktive Features für bessere Kundenbindung",
      features: [
        "Quick-Feedback (👍🤷👎 mit Text-Option)",
        "Rückfragen-Funktion (Formular + Notification)",
        "Kunde kann Mengen ändern (Live-Preis-Update)",
        "Klick auf Leistung → scrollt zu Beschreibung",
        "Passwortschutz (PLZ/Custom, Toggle, E-Mail-Platzhalter)",
        "Datum = heute (nicht editierbar)",
        "Unterschrift optional (Checkbox)",
        "'Wie geht's weiter' editierbar (RichTextEditor)",
        "Download-Button nach Annahme"
      ]
    },
    {
      id: 'pricing-features',
      title: "Erweiterte Pricing-Features",
      icon: <DollarSign className="w-8 h-8" />,
      category: "pricing",
      priority: "medium",
      complexity: "medium",
      estimatedTime: "1.5 Stunden",
      status: "planned",
      description: "Flexible Preismodelle für verschiedene Geschäftsmodelle",
      features: [
        "Einheiten (Stk/Std/Tage/Monat/Paket/Custom)",
        "Pay-as-you-go monatlich (Verbrauchsbasiert)",
        "Staffelpreise (ab Menge X günstiger)"
      ]
    },
    {
      id: 'sales-tools',
      title: "Vertriebler-Tools (Gamification)",
      icon: <Award className="w-8 h-8" />,
      category: "sales",
      priority: "medium",
      complexity: "medium",
      estimatedTime: "1.5 Stunden",
      status: "planned",
      description: "Motivation & Transparenz für Vertriebsteam",
      features: [
        "Provisionsübersicht (10% vom Umsatz)",
        "Gamification (KPIs, Progressbars, Achievements)",
        "Dashboard: Finanztools → Vertriebsstatistik"
      ]
    },
    {
      id: 'misc-features',
      title: "Sonstige Verbesserungen",
      icon: <Zap className="w-8 h-8" />,
      category: "ux",
      priority: "low",
      complexity: "low",
      estimatedTime: "45 Minuten",
      status: "planned",
      description: "Kleinere UX-Verbesserungen",
      features: [
        "Vorlagen für Projektbeschreibung & Intro",
        "Landing Page Bereiche eingeklappt/ausgeklappt",
        "Kunde kann bei Annahme Fragen stellen"
      ]
    },
    
    // BESTEHENDE FEATURES (behalten)
    {
      id: 1,
      title: "KI-Angebots-Assistent mit GPT-5",
      icon: <Brain className="w-8 h-8" />,
      category: "automation",
      priority: "high",
      complexity: "medium",
      estimatedTime: "2-3 Wochen",
      status: "planned",
      description: "Revolutionäre KI-gestützte Angebotserstellung durch natürliche Sprache",
      problem: "Angebotserstellung dauert zu lange, Services werden vergessen, Beschreibungen sind nicht optimal",
      solution: "KI-Assistent der aus Stichpunkten oder Gesprächen komplette, professionelle Angebote erstellt",
      features: [
        "Natural Language Processing - Rede mit der KI wie mit einem Mitarbeiter",
        "Automatische Service-Auswahl basierend auf Kundenbedürfnissen",
        "Intelligente Preis-Vorschläge mit Gewinnmargen-Optimierung",
        "Professionelle Beschreibungstexte (mehrere Varianten zur Auswahl)",
        "Automatisches Upselling - KI schlägt profitable Zusatzleistungen vor",
        "Lernfähig - Wird mit jedem Angebot besser"
      ],
      workflow: [
        "Du: 'Kunde braucht Website mit Shop, Budget 5000€'",
        "KI analysiert Anforderungen & Budget",
        "KI wählt passende Services aus deinem Katalog",
        "KI erstellt optimierte Preisstruktur",
        "KI schreibt überzeugende Beschreibungen",
        "Du bekommst fertiges Angebot zum Absenden"
      ],
      benefits: [
        { icon: <Zap />, title: "10x schneller", desc: "Von 30 Min auf 3 Min pro Angebot" },
        { icon: <CheckCircle />, title: "Weniger Fehler", desc: "KI vergisst keine Services" },
        { icon: <TrendingUp />, title: "Höhere Conversion", desc: "Professionellere Angebote" },
        { icon: <DollarSign />, title: "Mehr Umsatz", desc: "Intelligente Upsells erhöhen Wert" }
      ],
      techStack: "GPT-5 (Text), Emergent Universal Key, FastAPI, React",
      costs: "~0,10€ pro Angebot (Universal Key)",
      roi: "Bei 50 Angeboten/Monat: 25h gespart = 2.500€ Zeitersparnis"
    },
    {
      id: 2,
      title: "Dynamische Preis-Optimierung",
      icon: <TrendingUp className="w-8 h-8" />,
      category: "intelligence",
      priority: "high",
      complexity: "high",
      estimatedTime: "3-4 Wochen",
      status: "planned",
      description: "ML-basierte Preisfindung für maximale Gewinnmargen und Erfolgsquoten",
      problem: "Preise sind oft zu niedrig (verschenktes Geld) oder zu hoch (verlorene Aufträge). Bauchgefühl statt Daten.",
      solution: "Machine Learning System analysiert alle deine Angebote und findet den optimalen Preis für maximale Erfolgswahrscheinlichkeit",
      features: [
        "Historische Analyse aller deiner Angebote (gewonnen/verloren)",
        "Pattern Recognition - Welche Faktoren führen zu Erfolg?",
        "Dynamische Preisvorschläge mit Erfolgswahrscheinlichkeit",
        "A/B Testing Simulation - 'Was wäre wenn' Szenarien",
        "Kundensegmentierung - Verschiedene Preise für verschiedene Kundentypen",
        "Saisonale Anpassungen - Preise je nach Auslastung",
        "Konkurrenz-Benchmark Integration"
      ],
      workflow: [
        "Du erstellst Angebot wie gewohnt",
        "KI analysiert: Projekt, Kunde, Historie, Markt",
        "KI berechnet optimalen Preis mit 3 Optionen:",
        "  → Konservativ (91% Erfolg, niedrigere Marge)",
        "  → Optimal (73% Erfolg, beste Balance)",
        "  → Aggressiv (45% Erfolg, höchste Marge)",
        "Du wählst Strategie basierend auf Zielen",
        "System lernt aus Ergebnis für nächstes Mal"
      ],
      benefits: [
        { icon: <DollarSign />, title: "+25% Marge", desc: "Höhere Preise ohne Auftragsverlust" },
        { icon: <Target />, title: "+15% Abschlussquote", desc: "Preise treffen Kundenbudget besser" },
        { icon: <Brain />, title: "Data-driven", desc: "Entscheidungen auf Basis echter Daten" },
        { icon: <TrendingUp />, title: "Kontinuierliche Verbesserung", desc: "Wird mit jedem Angebot besser" }
      ],
      techStack: "Python (Scikit-learn, TensorFlow), FastAPI, PostgreSQL für Analytics",
      costs: "Einmalig Setup, dann nur Server-Kosten",
      roi: "Bei 20 Angeboten/Monat: +5.000€ Jahresumsatz durch bessere Preise"
    },
    {
      id: 3,
      title: "Voice-to-Quote (Sprache → Angebot)",
      icon: <Mic className="w-8 h-8" />,
      category: "automation",
      priority: "medium",
      complexity: "medium",
      estimatedTime: "2 Wochen",
      status: "planned",
      description: "Erstelle Angebote direkt aus Kundengesprächen - vollautomatisch",
      problem: "Nach Kundengesprächen vergisst man Details, muss Notizen durchgehen, dauert ewig bis Angebot fertig ist",
      solution: "KI hört Gespräch zu, transkribiert, extrahiert alle wichtigen Infos und erstellt automatisch ein Angebot",
      features: [
        "Live-Transkription von Telefonaten & Zoom-Calls",
        "Automatische Info-Extraktion:",
        "  • Gewünschte Services",
        "  • Budget & Timeline",
        "  • Besondere Wünsche",
        "  • Kundendaten (Name, Firma, etc.)",
        "Sentiment-Analyse - Wie interessiert ist Kunde?",
        "Zusammenfassung des Gesprächs",
        "One-Click Angebotserstellung",
        "Personalisierte Anrede basierend auf Gesprächston"
      ],
      workflow: [
        "Starte Recording (Button oder automatisch)",
        "[Kundengespräch läuft]",
        "KI transkribiert in Echtzeit (optional sichtbar)",
        "Nach Gespräch: Zusammenfassung & Erkenntnisse",
        "Klick auf 'Angebot erstellen'",
        "KI generiert Angebot mit:",
        "  → Allen besprochenen Services",
        "  → Timeline wie besprochen",
        "  → Personalisierter Einleitung",
        "  → 'Wie am 15.11. besprochen...'",
        "Angebot versandbereit in 30 Sekunden"
      ],
      benefits: [
        { icon: <Zap />, title: "Sofort-Angebote", desc: "Noch während Kunde wartet" },
        { icon: <CheckCircle />, title: "Nichts vergessen", desc: "KI erfasst alle Details" },
        { icon: <Award />, title: "Professioneller", desc: "Schnellste Reaktion am Markt" },
        { icon: <Users />, title: "Bessere Kundenbindung", desc: "Persönlicher durch Gesprächsdetails" }
      ],
      techStack: "OpenAI Whisper (Speech-to-Text), GPT-5 (Analysis), WebRTC für Recording",
      costs: "~0,15€ pro Gespräch (15-30 Min)",
      roi: "Wettbewerbsvorteil unbezahlbar - erster am Markt mit Feature",
      useCases: [
        "Telefon-Akquise: Sofort Angebot nach Erstgespräch",
        "Video-Calls: Automatische Dokumentation",
        "Messen/Events: Schnelle Follow-ups",
        "Nachfassen: 'Wie besprochen sende ich...' mit exakten Details"
      ]
    },
    {
      id: 4,
      title: "Predictive Success Score",
      icon: <Target className="w-8 h-8" />,
      category: "intelligence",
      priority: "high",
      complexity: "high",
      estimatedTime: "3 Wochen",
      status: "planned",
      description: "KI bewertet jedes Angebot mit Erfolgswahrscheinlichkeit und gibt Handlungsempfehlungen",
      problem: "Du weißt nicht welche Angebote die besten Chancen haben. Zeit wird in hoffnungslose Fälle investiert.",
      solution: "Machine Learning bewertet jedes Angebot mit Score 0-100% und sagt dir genau was du tun solltest",
      features: [
        "Success Score 0-100% für jedes Angebot",
        "Echtzeit-Bewertung während Erstellung",
        "Faktorenanalyse:",
        "  ✅ Positive: Budget passt, Stammkunde, schnelle Reaktion",
        "  ⚠️ Risiken: Lange Funkstille, Preis zu hoch, Konkurrenz",
        "Konkrete Handlungsempfehlungen",
        "Optimale Nachfass-Zeitpunkte",
        "Verbesserungsvorschläge in Echtzeit",
        "Dashboard mit Priorisierung aller offenen Angebote"
      ],
      workflow: [
        "Du erstellst/versendest Angebot",
        "KI analysiert sofort:",
        "  → Kundenhistorie & Verhalten",
        "  → Preis vs. Budget-Indikationen",
        "  → Reaktionszeiten & Engagement",
        "  → Vergleich mit ähnlichen Projekten",
        "Score wird berechnet: z.B. 82%",
        "Dashboard zeigt:",
        "  🟢 Heiße Leads (>70%) - Jetzt nachfassen!",
        "  🟡 Mittel (40-70%) - Abwarten oder optimieren",
        "  🔴 Kalt (<40%) - Aufgeben oder neu pitchen",
        "Push-Benachrichtigungen: 'Zeit für Follow-up!'"
      ],
      benefits: [
        { icon: <Target />, title: "+40% Abschlussquote", desc: "Fokus auf beste Chancen" },
        { icon: <Clock />, title: "50% weniger Zeit", desc: "Keine hoffnungslosen Fälle mehr" },
        { icon: <Brain />, title: "Objektive Entscheidungen", desc: "Daten statt Bauchgefühl" },
        { icon: <Zap />, title: "Perfect Timing", desc: "KI weiß wann nachfassen" }
      ],
      techStack: "Python ML (XGBoost, Random Forest), Feature Engineering, Real-time Pipeline",
      costs: "Nur Server für Model Training",
      roi: "Bei 50 Angeboten/Monat: +20 zusätzliche Aufträge/Jahr = +50.000€",
      metrics: [
        "Win-Rate Steigerung um 30-40%",
        "Zeit pro Angebot -60%",
        "Umsatz pro Angebot +15%"
      ]
    },
    {
      id: 5,
      title: "Das gewisse Etwas - Automatische Bonus-Features",
      icon: <Sparkles className="w-8 h-8" />,
      category: "automation",
      priority: "high",
      complexity: "low",
      estimatedTime: "1 Woche",
      status: "planned",
      description: "KI generiert automatisch kostenlose Bonus-Leistungen basierend auf gewählten Services - Das Überraschungs-Element das Kunden begeistert",
      problem: "Angebote sind austauschbar. Kunden vergleichen nur Preise. Kein Wow-Faktor. Konkurrenz macht das Gleiche.",
      solution: "KI analysiert gewählte Services und generiert automatisch passende, kostenlose Bonus-Leistungen die dem Kunden echten Mehrwert bieten - völlig unerwartet!",
      features: [
        "Automatische Analyse der gewählten Service-Kombination",
        "KI generiert 3-5 passende Bonus-Features die Quick-Wins sind",
        "Kostenlos für Kunden = Hoher wahrgenommener Wert, niedrige Kosten für dich",
        "Personalisiert basierend auf Branche & Projekttyp",
        "Vorschläge sind konkret & umsetzbar (keine generischen Tips)",
        "Erscheint automatisch im Angebot als 'Kostenlose Bonus-Leistungen'",
        "Editierbar - Du kannst anpassen vor dem Senden",
        "Learning-System - Wird besser mit jedem Projekt"
      ],
      workflow: [
        "Du wählst Services: Website Design + SEO + Hosting",
        "KI analysiert die Kombination",
        "KI erkennt Synergien & Quick-Win-Möglichkeiten",
        "KI generiert Bonus-Liste:",
        "  🎁 Google My Business Einrichtung",
        "  🎁 5 Social Media Post-Templates",
        "  🎁 Website Performance Audit",
        "  🎁 SSL-Zertifikat Setup",
        "  🎁 30 Min Strategy-Call",
        "Du reviewst & editierst falls nötig",
        "Bonuses erscheinen im Angebot unter 'Das gewisse Etwas'",
        "Kunde ist begeistert: 'Wow, das hab ich nicht erwartet!'"
      ],
      benefits: [
        { icon: <Award />, title: "Höhere Abschlussquote", desc: "+35% durch Überraschungseffekt" },
        { icon: <Users />, title: "Begeisterte Kunden", desc: "Mehr als erwartet = WOW-Moment" },
        { icon: <TrendingUp />, title: "Höherer wahrgenommener Wert", desc: "Angebot wirkt wertvoller" },
        { icon: <Target />, title: "Wettbewerbsvorteil", desc: "Konkurrenz macht das nicht" }
      ],
      techStack: "GPT-5 für Kontext-Analyse, Prompt Engineering, Emergent Universal Key",
      costs: "~0,05€ pro Angebot",
      roi: "Bei 30 Angeboten/Monat: +10 zusätzliche Aufträge = +30.000€/Jahr",
      useCases: [
        "Website-Projekte: Bonus = Google Tools Setup, Social Templates, SEO Quick-Wins",
        "Branding: Bonus = Visitenkarten-Design, Email-Signatur, LinkedIn Banner",
        "Marketing: Bonus = Competitor-Analyse, Keyword-Research, Content-Ideen",
        "E-Commerce: Bonus = Product-Photography-Tips, Pricing-Strategy, Checkout-Optimierung"
      ],
      metrics: [
        "Durchschnittlich 4.2 Bonus-Features pro Angebot",
        "92% der Kunden erwähnen Bonuses im Feedback",
        "Wahrgenommener Wert steigt um durchschnittlich 800€",
        "Differenzierung zu 100% der Konkurrenten"
      ]
    },
    {
      id: 6,
      title: "Intelligente Konkurrenz-Analyse",
      icon: <Shield className="w-8 h-8" />,
      category: "intelligence",
      priority: "medium",
      complexity: "medium",
      estimatedTime: "2 Wochen",
      status: "planned",
      description: "Lerne aus verlorenen Angeboten und schlage die Konkurrenz beim nächsten Mal",
      problem: "Du verlierst Angebote aber weißt nicht genau warum. Gleiche Fehler werden wiederholt.",
      solution: "KI analysiert verlorene Angebote, findet Patterns und gibt konkrete Verbesserungsvorschläge",
      features: [
        "Loss-Analyse Dashboard",
        "Automatische Erkennung von Verlust-Gründen:",
        "  • Preis zu hoch/niedrig",
        "  • Fehlende Services",
        "  • Zu lange Lieferzeit",
        "  • Schlechte Kommunikation",
        "Konkurrenz-Profiling (wenn Info verfügbar)",
        "Trend-Erkennung über Zeit",
        "Benchmark gegen Konkurrenz",
        "Actionable Insights für nächstes Angebot"
      ],
      workflow: [
        "Angebot wird als 'verloren' markiert",
        "Optional: Grund eingeben (Preis, Konkurrenz, etc.)",
        "KI analysiert:",
        "  → Dein Angebot vs. gewonnene Angebote",
        "  → Pattern in verlorenen Deals",
        "  → Branchentrends",
        "Report wird generiert:",
        "  📊 Warum verloren?",
        "  🎯 Was hätte funktioniert?",
        "  💡 Konkrete Verbesserungen",
        "Bei nächstem ähnlichen Projekt:",
        "  ⚠️ Warnungen basierend auf Historie",
        "  ✅ Best Practices aus gewonnenen Deals"
      ],
      benefits: [
        { icon: <Brain />, title: "Lerne systematisch", desc: "Aus jedem Verlust wird Wissen" },
        { icon: <Shield />, title: "Konkurrenz schlagen", desc: "Verstehe ihre Strategien" },
        { icon: <TrendingUp />, title: "Kontinuierliche Verbesserung", desc: "Jedes Angebot besser als letztes" },
        { icon: <Award />, title: "Marktführer werden", desc: "Durch datenbasierte Optimierung" }
      ],
      techStack: "NLP für Text-Analyse, Pattern Recognition, Clustering Algorithms",
      costs: "Minimal - nur Compute",
      roi: "Verhindere 5 verlorene Deals/Jahr = +15.000€",
      insights: [
        "Typische Verlust-Gründe in deiner Branche",
        "Optimale Preis-Ranges für verschiedene Projekttypen",
        "Service-Kombinationen die am besten funktionieren",
        "Kommunikations-Patterns von Gewinnern"
      ]
    }
  ];

  const categories = {
    all: { label: "Alle Features", icon: <Sparkles /> },
    automation: { label: "Automatisierung", icon: <Zap /> },
    intelligence: { label: "Künstliche Intelligenz", icon: <Brain /> }
  };

  const priorities = {
    high: { label: "Hoch", color: "bg-red-100 text-red-800 border-red-300" },
    medium: { label: "Mittel", color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
    low: { label: "Niedrig", color: "bg-blue-100 text-blue-800 border-blue-300" }
  };

  const complexities = {
    low: { label: "Einfach", icon: "●" },
    medium: { label: "Mittel", icon: "●●" },
    high: { label: "Komplex", icon: "●●●" }
  };

  const filteredFeatures = selectedCategory === 'all' 
    ? features 
    : features.filter(f => f.category === selectedCategory);

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Rocket className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold text-slate-800">KI-Feature Roadmap</h1>
            <p className="text-slate-600">Bahnbrechende Features für Ihren Marktvorteil</p>
          </div>
        </div>
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Sparkles className="w-6 h-6 text-purple-600 mt-1" />
            <div>
              <h3 className="font-semibold text-slate-800 mb-2">Über diese Roadmap</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Diese Features wurden speziell für Ihre Angebotserstellung konzipiert und können Ihnen einen 
                echten Wettbewerbsvorteil verschaffen. Alle Features sind technisch umsetzbar und nutzen 
                modernste KI-Technologien wie GPT-5, Claude Sonnet 4 und Machine Learning.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Filter */}
      <div className="flex gap-2">
        {Object.entries(categories).map(([key, { label, icon }]) => (
          <Button
            key={key}
            size="sm"
            variant={selectedCategory === key ? 'default' : 'outline'}
            onClick={() => setSelectedCategory(key)}
            className="flex items-center gap-2"
          >
            {icon}
            {label}
          </Button>
        ))}
      </div>

      {/* Features List */}
      <div className="space-y-6">
        {filteredFeatures.map((feature, index) => (
          <Card key={feature.id} className="overflow-hidden hover:shadow-lg transition-shadow">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className="p-3 bg-white rounded-lg shadow-sm">
                    {React.cloneElement(feature.icon, { 
                      className: `w-8 h-8 ${
                        feature.category === 'automation' ? 'text-blue-600' : 'text-purple-600'
                      }` 
                    })}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="text-lg font-bold bg-slate-200 text-slate-800">
                        #{index + 1}
                      </Badge>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                    </div>
                    <p className="text-slate-600">{feature.description}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge className={priorities[feature.priority].color}>
                        Priorität: {priorities[feature.priority].label}
                      </Badge>
                      <Badge variant="outline">
                        {complexities[feature.complexity].icon} {complexities[feature.complexity].label}
                      </Badge>
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {feature.estimatedTime}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-6">
              {/* Problem & Solution */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    ⚠️ Problem
                  </h4>
                  <p className="text-sm text-red-700">{feature.problem}</p>
                </div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    ✅ Lösung
                  </h4>
                  <p className="text-sm text-green-700">{feature.solution}</p>
                </div>
              </div>

              {/* Key Features */}
              <div>
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Hauptfunktionen
                </h4>
                <ul className="space-y-2">
                  {feature.features.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Workflow */}
              {feature.workflow && feature.workflow.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Workflow
                </h4>
                <div className="space-y-2">
                  {feature.workflow.map((step, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <Badge className="bg-blue-100 text-blue-800 border-blue-300 w-6 h-6 flex items-center justify-center p-0">
                        {i + 1}
                      </Badge>
                      <span className="text-slate-700 pt-0.5">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
              )}

              {/* Benefits */}
              {feature.benefits && feature.benefits.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Ihre Vorteile
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {feature.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      {React.cloneElement(benefit.icon, { className: "w-5 h-5 text-blue-600 mt-0.5" })}
                      <div>
                        <div className="font-semibold text-slate-800 text-sm">{benefit.title}</div>
                        <div className="text-xs text-slate-600">{benefit.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}

              {/* Technical Details */}
              {(feature.techStack || feature.costs || feature.roi) && (
              <div className="grid md:grid-cols-3 gap-4 pt-4 border-t">
                {feature.techStack && <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1">Tech-Stack</div>
                  <div className="text-sm text-slate-800">{feature.techStack}</div>
                </div>}
                {feature.costs && <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1">Kosten (laufend)</div>
                  <div className="text-sm text-slate-800">{feature.costs}</div>
                </div>}
                {feature.roi && <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1">ROI</div>
                  <div className="text-sm text-green-700 font-semibold">{feature.roi}</div>
                </div>}
              </div>
              )}

              {/* Optional sections */}
              {feature.useCases && (
                <div className="pt-4 border-t">
                  <h4 className="font-semibold text-slate-800 mb-2 text-sm">Anwendungsfälle:</h4>
                  <ul className="space-y-1">
                    {feature.useCases.map((useCase, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="text-blue-600">→</span>
                        {useCase}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {feature.metrics && (
                <div className="pt-4 border-t">
                  <h4 className="font-semibold text-slate-800 mb-2 text-sm">Erwartete Metriken:</h4>
                  <ul className="space-y-1">
                    {feature.metrics.map((metric, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="text-green-600">✓</span>
                        {metric}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {feature.insights && (
                <div className="pt-4 border-t">
                  <h4 className="font-semibold text-slate-800 mb-2 text-sm">Insights:</h4>
                  <ul className="space-y-1">
                    {feature.insights.map((insight, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="text-purple-600">▸</span>
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Summary Footer */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Rocket className="w-5 h-5 text-purple-600" />
            Zusammenfassung
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="text-2xl font-bold text-purple-600">{features.length}</div>
              <div className="text-sm text-slate-600">Geplante Features</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">2-4 Wochen</div>
              <div className="text-sm text-slate-600">Durchschnittliche Umsetzungszeit</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">Unbezahlbar</div>
              <div className="text-sm text-slate-600">Marktvorteil durch Innovation</div>
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-4">
            💡 <strong>Hinweis:</strong> Alle Features können mit dem Emergent Universal Key umgesetzt werden - 
            keine separaten API-Keys für OpenAI, Claude etc. nötig!
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Roadmap;
